export const LOBBY_BACKDROP_HTML = `
  <div class="lobby-bg" aria-hidden="true"></div>
`;

export const MENU_BACKDROP_HTML = `
  <div class="hub-bg hub-bg--loading" aria-hidden="true">
    <video
      class="hub-bg-video"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
      poster=""
    >
      <source src="/video/stadium-bg.webm" type="video/webm" />
      <source src="/video/stadium-bg.mp4" type="video/mp4" />
    </video>
    <div class="hub-bg-scrim"></div>
    <div class="hub-bg-stands"></div>
    <div class="hub-bg-lights"></div>
    <div class="hub-bg-beams"></div>
    <div class="hub-bg-spot" aria-hidden="true"></div>
    <div class="hub-bg-mist"></div>
    <div class="hub-bg-pitch"></div>
    <div class="hub-bg-grain"></div>
  </div>
`;

function bindBackgroundVideo(overlay: HTMLElement): () => void {
  const bg = overlay.querySelector(".hub-bg") as HTMLElement | null;
  const video = overlay.querySelector(".hub-bg-video") as HTMLVideoElement | null;
  if (!bg || !video) return () => {};

  const reveal = (): void => {
    bg.classList.remove("hub-bg--loading");
    bg.classList.add("hub-bg--ready");
  };

  const onReady = (): void => {
    bg.classList.add("hub-bg--has-video");
    void video.play().catch(() => {
      bg.classList.remove("hub-bg--has-video");
    });
    reveal();
  };

  const onFail = (): void => {
    bg.classList.remove("hub-bg--has-video");
    reveal();
  };

  video.addEventListener("loadeddata", onReady, { once: true });
  video.addEventListener("error", onFail, { once: true });

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    onReady();
  } else {
    window.setTimeout(() => {
      if (!bg.classList.contains("hub-bg--ready")) onFail();
    }, 1200);
  }

  return () => {
    video.removeEventListener("loadeddata", onReady);
    video.removeEventListener("error", onFail);
    video.pause();
  };
}

export function bindMenuAmbience(overlay: HTMLElement): () => void {
  const teardownVideo = bindBackgroundVideo(overlay);
  const spot = overlay.querySelector(".hub-bg-spot") as HTMLElement | null;

  let frame = 0;
  let pendingX = 0;
  let pendingY = 0;
  let lastX = -1;
  let lastY = -1;

  const flush = (): void => {
    frame = 0;
    if (!spot) return;

    const dx = pendingX - lastX;
    const dy = pendingY - lastY;
    if (dx * dx + dy * dy < 64) return;

    lastX = pendingX;
    lastY = pendingY;
    spot.style.transform = `translate3d(${pendingX}px, ${pendingY}px, 0) translate(-50%, -50%)`;

    const rect = overlay.getBoundingClientRect();
    const nx = ((pendingX - rect.left) / rect.width - 0.5) * 2;
    const ny = ((pendingY - rect.top) / rect.height - 0.5) * 2;
    overlay.style.setProperty("--tilt-x", `${nx * 0.35}`);
    overlay.style.setProperty("--tilt-y", `${ny * 0.35}`);
  };

  const onMove = (event: PointerEvent): void => {
    pendingX = event.clientX;
    pendingY = event.clientY;
    if (!frame) frame = requestAnimationFrame(flush);
  };

  overlay.addEventListener("pointermove", onMove, { passive: true });

  return () => {
    overlay.removeEventListener("pointermove", onMove);
    if (frame) cancelAnimationFrame(frame);
    teardownVideo();
  };
}
